import type {PropsWithChildren} from 'react'
import {useCallback, useEffect, useState} from 'react'
import {useLocation} from 'react-router'
import {checkLoginStatus} from '@freckle/educator-entities/ts/common/helpers/common-api-helper'
import {checkSessionCookie as apiCheckSessionCookie} from '@freckle/classroom/ts/api/session'
import {WithResources} from '@freckle/classroom/ts/common/components/with-resources'
import {displayAlert} from '@freckle/classroom/ts/common/helpers/helpers'
import {WithAuthStatus} from '@freckle/classroom/ts/common/routers/router-v2/with-auth-status'
import {Routes} from '@freckle/classroom/ts/common/routers/routes'
import {useTrackChromelessAccess} from '@freckle/classroom/ts/hooks/chromeless/access'
import {useTeacher} from '@freckle/classroom/ts/hooks/use-teacher'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'
import {setupRenaissanceKeepalive} from './../app-router'
import {RedirectSet} from './cookie-redirect'

const AUTH_CHECK_RETRY_PERIOD = 1000 * 60 * 15 // 15 minutes
const VALID_UNLOGGED_URLS = ['/login', '/signup', '/reset-by-email']
const LOGIN_PAGE_RETRIES = 16

type WrapperProps = PropsWithChildren<object>

type Props = WrapperProps & {
  teacher: TeacherAttrs
}

function MiddlewareInner({children, teacher}: Props) {
  const location = useLocation()
  const [navigateToLogin, setNavigateToLogin] = useState(false)

  const isOnValidUnloggedUrl = useCallback(
    () => VALID_UNLOGGED_URLS.includes(location.pathname),
    [location],
  )

  useEffect(() => {
    checkLoginStatus(AUTH_CHECK_RETRY_PERIOD, isOnValidUnloggedUrl, LOGIN_PAGE_RETRIES)
  })

  useTrackChromelessAccess()

  useEffect(() => {
    const checkSessionCookie = () => {
      if (isOnValidUnloggedUrl()) {
        return
      }

      apiCheckSessionCookie().catch(() => {
        const msg = `You have been disconnected from Freckle. Please login again.`
        displayAlert('Please login', msg, () => {
          document.location.reload()
        })
      })
    }

    async function componentDidMount() {
      try {
        setupRenaissanceKeepalive(teacher)
      } catch {
        setNavigateToLogin(true)
      }
      /* When the tab is opened, it will check with the API if the teacher is
       * still logged. If not a popup will show up and ask him to log again */
      window.addEventListener('focus', checkSessionCookie)
    }
    componentDidMount()

    return () => window.removeEventListener('focus', checkSessionCookie, true)
    // eslint-disable-next-line
  }, []) // we want to run this effect only once (after component mounts)

  if (navigateToLogin) {
    return (
      <RedirectSet to={location.search ? `${Routes.login()}${location.search}` : Routes.login()} />
    )
  } else {
    return <>{children}</>
  }
}

export const MiddlewareWrapper = (props: WrapperProps) => {
  const location = useLocation()
  return (
    <WithAuthStatus
      render={({loggedIn}) =>
        loggedIn ? (
          <Middleware {...props} />
        ) : (
          <RedirectSet
            to={location.search ? `${Routes.login()}${location.search}` : Routes.login()}
          />
        )
      }
    />
  )
}

function Middleware(props: WrapperProps) {
  const teacher = useTeacher()
  return (
    <WithResources
      resources={{teacher}}
      render={({teacher}) => {
        return <MiddlewareInner teacher={teacher} {...props} />
      }}
      noSpinner
    />
  )
}
